/**
 * Stage 6 — evaluation and the Validation Report.
 *
 * The numbers (coverage, counts, defects, readiness) are computed by the
 * platform and shown first; the AI-written narrative follows. If the model's
 * own recommendation disagreed with the computed one, that is said out loud.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Menu,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconBrandAzure,
  IconCloudUpload,
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { BlobFilePicker, type BlobSelection } from "@/features/ai/BlobFilePicker";
import { ReportView } from "@/features/ai/ReportView";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import {
  downloadArtefact,
  exportArtefact,
  pushDefectToAdo,
  pushRunToAdo,
  type RunOut,
} from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { READINESS_COLOR, READINESS_LABEL, riskColor } from "../stageMeta";
import { Provenance } from "./Provenance";

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function ReportStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [busy, setBusy] = useState<string | null>(null);
  const [folder, setFolder] = useState<BlobSelection | null>(null);
  const [filename, setFilename] = useState(`${slug(run.title)}-validation-report`);
  const [adoConnections, setAdoConnections] = useState<ConnectionOut[]>([]);
  const [adoConnectionId, setAdoConnectionId] = useState<string | null>(null);

  const ev = run.evaluation;

  const loadAdo = useCallback(async () => {
    if (!run.is_owner) return;
    try {
      const page = await listConnections(await getToken(), "azure_devops");
      setAdoConnections(page.items);
      setAdoConnectionId((prev) => prev ?? page.items[0]?.connection_id ?? null);
    } catch {
      // A missing ADO connection is not an error — the push button stays hidden.
    }
  }, [getToken, run.is_owner]);

  useEffect(() => {
    void loadAdo();
  }, [loadAdo]);

  const [parentWorkItem, setParentWorkItem] = useState("");

  const pushRun = async () => {
    if (!adoConnectionId) return;
    setBusy("ado-run");
    try {
      const parent = parentWorkItem.trim() ? Number(parentWorkItem.trim()) : null;
      onUpdated(
        await pushRunToAdo(await getToken(), run.run_id, adoConnectionId, {
          parentWorkItemId: parent && Number.isFinite(parent) ? parent : null,
        }),
        false,
      );
      toastSuccess("Run pushed to Azure DevOps", "A validation work item was created.");
    } catch (err) {
      toastApiError("Could not push the run", err);
    } finally {
      setBusy(null);
    }
  };

  const pushDefect = async (defectId: string) => {
    if (!adoConnectionId) return;
    setBusy(`ado-${defectId}`);
    try {
      onUpdated(
        await pushDefectToAdo(await getToken(), run.run_id, defectId, adoConnectionId),
        false,
      );
      toastSuccess("Pushed to Azure DevOps", "A work item was created and linked to the defect.");
    } catch (err) {
      toastApiError("Could not push to Azure DevOps", err);
    } finally {
      setBusy(null);
    }
  };

  const download = async (artefact: "plan" | "report", fmt: "md" | "pdf") => {
    setBusy("dl");
    try {
      const blob = await downloadArtefact(await getToken(), run.run_id, artefact, fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug(run.title)}-${artefact}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError("Could not download", err);
    } finally {
      setBusy(null);
    }
  };

  const exportToBlob = async (fmt: "md" | "pdf") => {
    if (!folder) return;
    setBusy("export");
    try {
      const fresh = await exportArtefact(await getToken(), run.run_id, {
        artefact: "report",
        fmt,
        container: folder.container,
        path: `${folder.path}${filename.replace(/\.(md|pdf)$/i, "")}.${fmt}`,
      });
      onUpdated(fresh, false);
      toastSuccess("Saved to Blob", fresh.report_saved_blob_path ?? "");
    } catch (err) {
      toastApiError("Could not save to Blob", err);
    } finally {
      setBusy(null);
    }
  };

  if (!run.report_md || !ev) {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        No report yet. When every test is closed, evaluate the run from the Execution tab.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="sm">
            <Text fw={600}>Validation Report</Text>
            {run.readiness && (
              <Badge variant="filled" size="lg" color={READINESS_COLOR[run.readiness]}>
                {READINESS_LABEL[run.readiness]}
              </Badge>
            )}
          </Group>
          <Provenance
            promptVersion={run.report_prompt_version}
            inferenceId={run.report_inference_id}
            extra={`evaluated ${new Date(ev.computed_at_utc).toLocaleString()}`}
          />
        </Stack>
        <Menu>
          <Menu.Target>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              loading={busy === "dl"}
            >
              Download
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Validation report</Menu.Label>
            <Menu.Item onClick={() => void download("report", "pdf")}>PDF</Menu.Item>
            <Menu.Item onClick={() => void download("report", "md")}>Markdown</Menu.Item>
            <Menu.Label>Validation plan</Menu.Label>
            <Menu.Item onClick={() => void download("plan", "pdf")}>PDF</Menu.Item>
            <Menu.Item onClick={() => void download("plan", "md")}>Markdown</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {ev.readiness_mismatch && (
        <Alert variant="light" color="orange" icon={<IconAlertTriangle size={16} />}>
          The model's narrative recommended <b>{ev.model_readiness?.toUpperCase()}</b>; the computed
          verdict is <b>{READINESS_LABEL[ev.computed_readiness]}</b>. The computed verdict is
          authoritative — read the narrative with that in mind.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 2, md: 5 }} spacing="sm">
        <Kpi label="Tests" value={ev.tests.total} />
        <Kpi label="Passed" value={ev.tests.passed} color="green" />
        <Kpi label="Failed" value={ev.tests.failed} color={ev.tests.failed ? "red" : undefined} />
        <Kpi
          label="Blocked / open"
          value={ev.tests.blocked + ev.tests.open}
          color={ev.tests.blocked + ev.tests.open ? "orange" : undefined}
        />
        <Kpi
          label="Defects"
          value={ev.defects.length}
          color={ev.defects.length ? "red" : undefined}
        />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md">
        <Text fw={600} mb="xs">
          Risk coverage
        </Text>
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Risk</Table.Th>
              <Table.Th>GxP</Table.Th>
              <Table.Th>Tests</Table.Th>
              <Table.Th>Covered</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ev.risks.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <Text size="sm">
                    <b>{r.id}</b> {r.title}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={riskColor(r.gxp_risk)}>
                    {r.gxp_risk}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">
                    {r.tests.length ? r.tests.join(", ") : "—"}
                    {r.failed_tests.length ? ` (failed: ${r.failed_tests.join(", ")})` : ""}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={r.covered ? "green" : "red"}>
                    {r.covered ? "yes" : "no"}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {run.is_owner && (
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" align="flex-end">
            <div>
              <Text fw={600}>Azure DevOps</Text>
              <Text size="xs" c="dimmed">
                Push this validation run as a work item; defects hang under it. Optionally parent it
                under an RfC / requirement work item.
              </Text>
            </div>
            {run.ado_work_item_url ? (
              <Anchor href={run.ado_work_item_url} target="_blank" size="sm">
                <Group gap={4}>
                  <IconExternalLink size={14} /> Work item #{run.ado_work_item_id}
                </Group>
              </Anchor>
            ) : adoConnections.length > 0 ? (
              <Group gap="xs" align="flex-end">
                <Select
                  size="xs"
                  label="Connection"
                  data={adoConnections.map((c) => ({
                    value: c.connection_id,
                    label: c.display_name,
                  }))}
                  value={adoConnectionId}
                  onChange={setAdoConnectionId}
                  w={200}
                />
                <TextInput
                  size="xs"
                  label="Parent work item (optional)"
                  placeholder="RfC id, e.g. 1234"
                  value={parentWorkItem}
                  onChange={(e) => setParentWorkItem(e.currentTarget.value)}
                  w={160}
                />
                <Button
                  size="xs"
                  leftSection={<IconBrandAzure size={14} />}
                  disabled={!adoConnectionId}
                  loading={busy === "ado-run"}
                  onClick={() => void pushRun()}
                >
                  Push run
                </Button>
              </Group>
            ) : (
              <Text size="xs" c="dimmed">
                Add an Azure DevOps connection under Integrations.
              </Text>
            )}
          </Group>
        </Paper>
      )}

      {run.defects.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Defects ({run.defects.length})</Text>
            {run.is_owner && adoConnections.length > 0 && (
              <Select
                size="xs"
                placeholder="Azure DevOps connection"
                data={adoConnections.map((c) => ({
                  value: c.connection_id,
                  label: c.display_name,
                }))}
                value={adoConnectionId}
                onChange={setAdoConnectionId}
                w={240}
                aria-label="Azure DevOps connection for defect push"
              />
            )}
          </Group>
          <Table verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Defect</Table.Th>
                <Table.Th>Severity</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Azure DevOps</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {run.defects.map((d) => (
                <Table.Tr key={d.defect_id}>
                  <Table.Td>
                    <Badge variant="outline" color="gray" size="sm">
                      {d.code}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={
                        d.severity === "critical"
                          ? "red"
                          : d.severity === "major"
                            ? "orange"
                            : "gray"
                      }
                    >
                      {d.severity}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{d.title}</Text>
                    {d.description && (
                      <Text size="xs" c="dimmed">
                        {d.description}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">
                      {d.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {d.external_ref ? (
                      <Anchor href={d.external_ref} target="_blank" size="xs">
                        <Group gap={4}>
                          <IconExternalLink size={12} /> work item
                        </Group>
                      </Anchor>
                    ) : run.is_owner ? (
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconBrandAzure size={12} />}
                        disabled={!adoConnectionId}
                        loading={busy === `ado-${d.defect_id}`}
                        onClick={() => void pushDefect(d.defect_id)}
                      >
                        Push
                      </Button>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {run.is_owner && adoConnections.length === 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              Add an Azure DevOps connection under Integrations to push defects as work items.
            </Text>
          )}
        </Paper>
      )}

      <Paper withBorder radius="md" p="lg">
        <ScrollArea.Autosize mah={720}>
          <ReportView text={run.report_md} />
        </ScrollArea.Autosize>
      </Paper>

      {run.blob_connection_id && run.is_owner && (
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="xs">
            Save the report to Blob
          </Text>
          <BlobFilePicker
            connectionId={run.blob_connection_id}
            label="Destination folder"
            mode="folder"
            value={folder}
            onChange={setFolder}
          />
          <Group align="flex-end" gap="xs" mt="xs">
            <TextInput
              label="Filename"
              value={filename}
              onChange={(e) => setFilename(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              variant="light"
              leftSection={<IconCloudUpload size={16} />}
              disabled={!folder || !filename.trim()}
              loading={busy === "export"}
              onClick={() => void exportToBlob("pdf")}
            >
              Save as PDF
            </Button>
            <Button
              variant="light"
              disabled={!folder || !filename.trim()}
              loading={busy === "export"}
              onClick={() => void exportToBlob("md")}
            >
              Save .md
            </Button>
          </Group>
          {run.report_saved_blob_path && (
            <Text size="xs" c="dimmed" mt={4}>
              Last saved to {run.report_saved_blob_path}
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}

function Kpi({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} size="xl" c={color}>
        {value}
      </Text>
    </Paper>
  );
}

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "validation-run"
  );
}
